import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiRequestError } from '../lib/api.js';
import { useChildLogin, useChildPicker } from '../lib/session.js';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Four taps to get in. When it is wrong the dots simply clear and a warm line
 * appears - no red, no buzz, no shake. Getting it wrong is another go, here as
 * everywhere else in this app.
 */
export function ChildPin() {
  const { childId = '' } = useParams();
  const navigate = useNavigate();
  const login = useChildLogin();
  const { data: children } = useChildPicker();
  const [pin, setPin] = useState('');

  const child = children?.find((c) => c.id === childId);
  const busy = login.isPending;

  const press = (digit: string) => {
    if (busy) return;
    const next = pin + digit;
    if (next.length < 4) {
      setPin(next);
      return;
    }
    setPin(next);
    login.mutate(
      { childId, pin: next },
      {
        onSuccess: () => navigate('/home', { replace: true }),
        onError: () => setPin(''),
      },
    );
  };

  let note: string | null = null;
  if (login.error instanceof ApiRequestError) {
    note =
      login.error.status === 429
        ? 'Let us take a short break and try again in a few minutes.'
        : login.error.body.message;
  }

  return (
    <main className="screen">
      <h1>{child ? `Hello, ${child.nickname}!` : 'Hello!'}</h1>
      <p>Tap your four numbers.</p>

      <div className="pin-dots" aria-label={`${pin.length} of 4 numbers entered`}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="pin-dot" data-filled={i < pin.length} />
        ))}
      </div>

      {note && <p className="gentle-note">{note}</p>}

      <div className="keypad">
        {KEYS.map((k) => (
          <button key={k} type="button" className="key" disabled={busy} onClick={() => press(k)}>
            {k}
          </button>
        ))}
        <button type="button" className="key key--wide" onClick={() => setPin('')} disabled={busy}>
          clear
        </button>
        <button type="button" className="key" disabled={busy} onClick={() => press('0')}>
          0
        </button>
        <button
          type="button"
          className="key key--wide"
          disabled={busy}
          onClick={() => setPin((p) => p.slice(0, -1))}
        >
          back
        </button>
      </div>

      <button type="button" className="btn btn--quiet" onClick={() => navigate('/')}>
        Not me
      </button>
    </main>
  );
}
